import { Slide } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MobileStepper from "@mui/material/MobileStepper";
import { styled, useTheme } from "@mui/material/styles";
import { MdsKeyboardArrowLeft, MdsKeyboardArrowRight } from "@sinch/icons";
import * as React from "react";
import { Children, memo, PropsWithChildren, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";

// Types
type Direction = "forward" | "backward";

interface CarouselProps {
  height: string;
}

export const Carousel = memo<PropsWithChildren<CarouselProps>>(({ children, height }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const containerRef = useRef<HTMLElement>(null);

  const [activeChange, setActiveChange] = useState<[number, Direction]>([0, "forward"]);
  const [activeStep, direction] = activeChange;

  const arrayChildren = Children.toArray(children);
  const maxSteps = arrayChildren.length;

  const handleNext = useCallback(() => {
    setActiveChange(([prevStep]) => [prevStep + 1, "forward"]);
  }, []);

  const handleBack = useCallback(() => {
    setActiveChange(([prevStep]) => [prevStep - 1, "backward"]);
  }, []);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === maxSteps - 1;
  const isRtl = theme.direction === "rtl";

  const BackIcon = isRtl ? MdsKeyboardArrowRight : MdsKeyboardArrowLeft;
  const NextIcon = isRtl ? MdsKeyboardArrowLeft : MdsKeyboardArrowRight;

  return (
    <CarouselRoot ref={containerRef}>
      <SlideContainer height={height}>
        {arrayChildren.map((step, index) => (
          <Slide
            key={index}
            appear={false}
            container={containerRef.current}
            direction={getSlideDirection(direction, index, activeStep)}
            easing={{
              enter: undefined,
              exit: undefined,
            }}
            in={activeStep === index}
            mountOnEnter
            unmountOnExit
          >
            <SlideContent>{step}</SlideContent>
          </Slide>
        ))}
      </SlideContainer>
      <MobileStepper
        activeStep={activeStep}
        backButton={
          <Button disabled={isFirstStep} onClick={handleBack} size="small">
            <MdsIcon icon={BackIcon} />
            {t("Action.back")}
          </Button>
        }
        nextButton={
          <Button disabled={isLastStep} onClick={handleNext} size="small">
            {t("Action.next")}
            <MdsIcon icon={NextIcon} />
          </Button>
        }
        position="static"
        steps={maxSteps}
        sx={{ width: "fit-content", display: "flex", gap: "16px", background: "transparent" }}
        variant="dots"
      />
    </CarouselRoot>
  );
});

Carousel.displayName = "Carousel";

// Helper function to determine slide direction
const getSlideDirection = (direction: Direction, currentIndex: number, activeStep: number): "left" | "right" => {
  const shouldGoLeft = direction === "forward" ? currentIndex >= activeStep : currentIndex > activeStep;
  return shouldGoLeft ? "left" : "right";
};

// Styled components
const CarouselRoot = styled(Box)({
  flexGrow: 1,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const SlideContainer = styled(Box)<{ height: string }>(({ height }) => ({
  height,
  position: "relative",
  overflow: "hidden",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

const SlideContent = styled(Box)({
  position: "absolute",
  maxHeight: "100%",
  height: "100%",
  overflow: "auto",
});
